import { supabase } from "@/services/supabase"
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { Property } from "../types/property"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

type ImageTransformOptions = {
  width?: number;
  height?: number;
  resize?: 'cover' | 'contain' | 'fill';
  quality?: number;
  format?: 'origin';
}

// Utility to set page title with consistent formatting
export function setPageTitle(title?: string) {
  if (!title) {
    document.title = 'Swim Homes | Simplified Home Selling';
  } else {
    document.title = `${title} | Swim Homes`;
  }
}

/**
 * Enhanced utility to set both page title and meta description
 * @param options Configuration object for page metadata
 */
export function setPageMetadata(options: {
  title?: string;
  description?: string;
  ogImage?: string;
}) {
  // Set page title
  if (!options.title) {
    document.title = 'Swim Homes | Simplified Home Selling';
  } else {
    document.title = `${options.title} | Swim Homes`;
  }

  // Set meta description if provided
  if (options.description) {
    // Find existing meta description tag or create a new one
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', options.description);

    // Also update Open Graph description
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', options.description);
    }

    // Update Twitter description
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) {
      twitterDescription.setAttribute('content', options.description);
    }
  }

  // Set Open Graph title
  if (options.title) {
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', `${options.title} | Swim Homes`);
    }

    // Update Twitter title
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) {
      twitterTitle.setAttribute('content', `${options.title} | Swim Homes`);
    }
  }

  // Set custom OG image if provided
  if (options.ogImage) {
    const ogImage = document.querySelector('meta[property="og:image"]');
    if (ogImage) {
      ogImage.setAttribute('content', options.ogImage);
    }

    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) {
      twitterImage.setAttribute('content', options.ogImage);
    }
  }
}

export function getImageUrl(path: string, options?: ImageTransformOptions) {
  if (!path) {
    return '';
  }

  // If path is already a full URL, check if it's a Supabase URL
  if (path.startsWith('https://')) {
    // Check if it's a Supabase storage URL that we can optimize
    const supabaseStoragePattern = /https:\/\/\w+\.supabase\.(co|in)\/storage\/v1\/object\/public\/images\/(.*)/;
    const match = path.match(supabaseStoragePattern);

    if (match) {
      // Extract the path after 'images/'
      const extractedPath = match[2];
      return getImageUrl(extractedPath, options);
    }

    // For other URLs, return as is
    return path;
  }

  const { data } = supabase.storage
    .from('images')
    .getPublicUrl(path, {
      // transform: {
      //   width: options?.width,
      //   height: options?.height,
      //   resize: options?.resize,
      //   quality: options?.quality || 80, // Default to 80% quality for optimization
      //   format: options?.format,
      // },
    });

  return data.publicUrl;
}

export function formatDate(dateString: string) {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
}


export function formatAddress(listing: Property): string {
  return `${listing.street_number} ${listing.street_name} ${listing.street_abbrev}, ${listing.municipality}`;
}

export function debounce<T>(fn: (arg: T) => Promise<void>, wait: number) {
  let timeout: NodeJS.Timeout;

  return (arg: T) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      fn(arg);
    }, wait);
  };
}

export function formatFullAddress(property: Property) {
  if (!property) return '';

  const streetNumber = property.street_number;
  const streetName = property.street_name +
    (property.street_abbrev ? ` ${property.street_abbrev}` : '');
  const city = property.municipality?.replace('Town of ', '');
  const postalCode = property.postal_code?.replace(/([A-Z]\d[A-Z])?(\d[A-Z]\d)/, '$1 $2');

  if (!streetNumber || !streetName || !city || !postalCode) return '';

  return `${streetNumber} ${streetName}, ${city}, ON ${postalCode}`.trim();
};

export function formatPrice(price: number | null | undefined): string {
  return price != null ? `$${price.toLocaleString()}` : 'Price on request';
}

// Helper function to format map address (simplified for geocoding)
export function formatMapAddress(property: Property) {
  if (!property) return '';

  const streetNumber = property.street_number;
  const streetName = property.street_name;
  const postalCode = property.postal_code?.replace(/([A-Z]\d[A-Z])?(\d[A-Z]\d)/, '$1 $2');

  if (!streetNumber || !streetName || !postalCode) return '';

  return `${streetNumber} ${streetName} ON ${postalCode}`.trim();
};